import { ReactElement, useEffect } from 'react';
import { useMarketDayContext } from 'utils/marketPeriods/MarketPeriodsContext';
import {
  convertDateToMarketDay,
  getCurrentMarketDay
} from 'utils/marketPeriods/marketDay';
import { useMarketPeriods } from 'utils/marketPeriods/useMarketPeriods';
import {
  getCurrentMarketPeriod,
  getMarketDayIntervalFromMarketPeriods
} from 'utils/marketPeriods/marketPeriods';
import {
  EventDetail,
  MarketDayEventEnum,
  MILESTONES_TRACKING_TICK
} from 'utils/marketDayMilestonesTracking/marketDayMilestonesTrackingUtils';
import { createAndDispatchCustomEvent } from 'utils/events/eventUtils';

const {
  MARKET_DAY_SECOND_CHANGED,
  MARKET_DAY_HALF_MINUTE_CHANGED,
  MARKET_DAY_MINUTE_CHANGED,
  MARKET_DAY_PERIOD_CHANGED,
  MARKET_DAY_HOUR_CHANGED,
  MARKET_DAY_CHANGED
} = MarketDayEventEnum;

// ==============================|| MARKET DAY MILESTONES TRACKING ||============================== //

export const MarketDayMilestonesTracking = (): ReactElement => {
  const { getMarketPeriodsForMarketDay } = useMarketDayContext();
  const {
    marketPeriodsForMarketDay: marketPeriods,
    marketHoursForMarketDay: marketHours
  } = useMarketPeriods();

  useEffect(() => {
    const currentMarketDay = getCurrentMarketDay();
    getMarketPeriodsForMarketDay(currentMarketDay);
  }, [getMarketPeriodsForMarketDay]);

  useEffect(() => {
    if (!marketPeriods || !marketHours) return;

    let previousMarketPeriod = getCurrentMarketPeriod(marketPeriods);
    let previousMarketHour = getCurrentMarketPeriod(marketHours);
    let previousMarketDayInterval =
      getMarketDayIntervalFromMarketPeriods(marketPeriods);

    const marketDayMilestonesTracker = setInterval(() => {
      const currentMarketTimestamp = new Date();
      const currentMarketDay = convertDateToMarketDay(currentMarketTimestamp);
      const currentMarketDayInterval =
        getMarketDayIntervalFromMarketPeriods(marketPeriods);
      const currentMarketPeriod = getCurrentMarketPeriod(marketPeriods);
      const currentMarketHour = getCurrentMarketPeriod(marketHours);

      const marketDayHalfMinuteChanged =
        [0, 30].indexOf(currentMarketTimestamp.getSeconds()) > -1;
      const marketDayMinuteChanged = currentMarketTimestamp.getSeconds() === 0;
      const marketDayPeriodChanged =
        previousMarketPeriod?.timeUnitNumber !==
        currentMarketPeriod?.timeUnitNumber;
      const marketDayHourChanged =
        previousMarketHour?.timeUnitNumber !==
        currentMarketHour?.timeUnitNumber;
      const marketDayChanged =
        previousMarketDayInterval?.marketDayStart !==
        currentMarketDayInterval?.marketDayStart;

      const eventDetail: EventDetail = {
        currentMarketTimestamp,
        currentMarketPeriod,
        currentMarketHour,
        currentMarketDay
      };

      createAndDispatchCustomEvent(MARKET_DAY_SECOND_CHANGED, eventDetail);

      if (marketDayHalfMinuteChanged) {
        createAndDispatchCustomEvent(
          MARKET_DAY_HALF_MINUTE_CHANGED,
          eventDetail
        );
      }

      if (marketDayMinuteChanged) {
        createAndDispatchCustomEvent(MARKET_DAY_MINUTE_CHANGED, eventDetail);
      }

      if (marketDayPeriodChanged) {
        createAndDispatchCustomEvent(MARKET_DAY_PERIOD_CHANGED, eventDetail);
        previousMarketPeriod = currentMarketPeriod;
      }

      if (marketDayHourChanged) {
        createAndDispatchCustomEvent(MARKET_DAY_HOUR_CHANGED, eventDetail);
        previousMarketHour = currentMarketHour;
      }

      if (marketDayChanged) {
        createAndDispatchCustomEvent(MARKET_DAY_CHANGED, eventDetail);
        previousMarketDayInterval = currentMarketDayInterval;
      }
    }, MILESTONES_TRACKING_TICK);

    return () => clearInterval(marketDayMilestonesTracker);
  }, [marketHours, marketPeriods]);

  return <></>;
};
